import { useEffect, useState } from "react";

import { useScrollLock } from "../useScrollLock";
import { AdminError, fetchFile } from "./api";

interface Loaded {
  url: string;
  type: string;
  size: number;
}

/**
 * Podgląd pliku przed pobraniem: PDF w ramce, skan jako obraz.
 *
 * Plik i tak trzeba pobrać żądaniem z kluczem, więc zamiast od razu zapisywać go
 * na dysk, pokazujemy zawartość z pamięci — konsultant sprawdza, czy to właściwy
 * dokument, i dopiero potem decyduje. Adres blob żyje tylko na czas podglądu.
 */
export function FilePreview({
  path,
  title,
  filename,
  init,
  onClose,
}: {
  path: string;
  title: string;
  filename: string;
  init?: RequestInit;
  onClose: () => void;
}) {
  const [file, setFile] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useScrollLock();

  useEffect(() => {
    let revoked = false;
    let url: string | null = null;

    fetchFile(path, init)
      .then((blob) => {
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setFile({ url, type: blob.type, size: blob.size });
      })
      .catch((caught) => setError((caught as AdminError).message));

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = () => {
    if (!file) return;
    const link = document.createElement("a");
    link.href = file.url;
    link.download = filename;
    link.click();
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}
         onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal__card modal__card--preview">
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          {file && <span className="adm__muted">{Math.round(file.size / 1024)} kB</span>}
          <button className="btn btn--secondary" disabled={!file} onClick={save}>
            Pobierz
          </button>
          <button
            className="btn btn--secondary"
            disabled={!file}
            onClick={() => file && window.open(file.url, "_blank")}
          >
            Nowa karta
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            Zamknij
          </button>
        </div>

        <div className="preview" data-ybug-sensitive>
          {error && <p className="error preview__msg">{error}</p>}

          {!error && !file && (
            <div className="preview__msg">
              <div className="spinner" />
              <p className="adm__muted">Przygotowuję podgląd…</p>
            </div>
          )}

          {file && file.type.startsWith("image/") && (
            <img className="preview__image" src={file.url} alt={title} />
          )}

          {file && file.type === "application/pdf" && (
            <iframe className="preview__frame" src={file.url} title={title} />
          )}

          {file && !file.type.startsWith("image/") && file.type !== "application/pdf" && (
            <p className="preview__msg adm__muted">
              Tego formatu nie da się pokazać w przeglądarce ({file.type || "nieznany typ"}).
              Pobierz plik, żeby go otworzyć.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
